import { IBiddingSignUpData, IBiddingSignUpKeys } from '@frontend/api';
import { Translate } from '@frontend/translation';
import { Button, CheckBox, Link, TextField } from '@frontend/ui-elements';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, useTheme } from '@mui/material';
import { head } from 'lodash';
import { ChangeEvent, useEffect } from 'react';
import { ControllerRenderProps, useController, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  MAX_NUMBER_AFTER_DECIMAL,
  MIN_QUANTITY_REQUIRED_MT,
} from '@customer-platform-shared/components/Forms/Forms.const';
import {
  CaptionBox,
  FieldsWrapper,
  FormBox,
  FormContainer,
} from './SignUpBiddingForm.css';
import {
  SignUpBiddingFormProps,
  ISignUpBiddingFormSchemaTranslations,
} from './SignUpBiddingForm.types';
import getSignUpBiddingFormSchema from './SignUpBiddingForm.utils';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import { useFormTracking } from '@customer-platform-shared/components/Forms/utils/useFormTracking';
import { NameBox } from '@customer-platform-shared/components/Forms/CustomerForm/CustomerForm.css';
import PhoneField from '@customer-platform-shared/components/ui-csr/PhoneField/PhoneField';

export default function SignUpBiddingForm({
  uuid,
  errors,
  isSuccess,
  isPending,
  onSubmit,
}: SignUpBiddingFormProps) {
  const intl = useIntl();
  const theme = useTheme();

  const maxNumbersAfterDecimal = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.number.max_numbers_after_decimal',
    },
    { MAX_NUMBER_AFTER_DECIMAL: MAX_NUMBER_AFTER_DECIMAL.toString() },
  );
  const minQuantityRequired = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.quantify.min-quantity-required',
    },
    { MIN_QUANTITY_REQUIRED_MT: MIN_QUANTITY_REQUIRED_MT.toString() },
  );
  const required = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.required',
  });
  const requiredCheckbox = intl.formatMessage({
    id: 'customer-platform.biddable-listing-page.bidding-form.errors.checkbox-required',
  });

  const dateMustBeFuture = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.datepicker.date_past',
  });

  const emailInvalidMsg = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.email.invalid',
  });

  const phoneNumberInvalidMsg = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.phone_number.invalid',
  });

  const signUpBiddingFormSchema = getSignUpBiddingFormSchema({
    required,
    requiredCheckbox,
    maxNumbersAfterDecimal,
    minQuantityRequired,
    dateMustBeFuture,
    emailInvalidMsg,
    phoneNumberInvalidMsg,
  } as ISignUpBiddingFormSchemaTranslations);

  const {
    control,
    formState: { errors: formErrors },
    handleSubmit,
    register,
    reset,
    setError,
    clearErrors,
  } = useForm<FormType<IBiddingSignUpData>>({
    defaultValues: {
      listingUuid: uuid,
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      phoneNumber: '',
      price: '',
      quantity: '',
      termsAgreed: false,
    },
    resolver: yupResolver<FormType<IBiddingSignUpData>>(
      signUpBiddingFormSchema,
    ),
  });

  const formId = 'bidding-form.form';

  const { field: termsAgreedField } = useController({
    name: 'termsAgreed',
    control,
  });
  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>) => {
    termsAgreedField.onChange(event.target.checked);
  };

  const { field: phoneNumberField } = useController({
    name: 'phoneNumber',
    control,
  });

  const handleStringChange =
    (field: ControllerRenderProps) => (value: string) => {
      field.onChange(value);
    };

  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) {
      clearErrors();
      return;
    }
    Object.entries(errors).forEach(([field, messages]) =>
      setError(field as IBiddingSignUpKeys, {
        type: 'manual',
        message: (messages?.[0] ?? '') as string,
      }),
    );
  }, [errors, setError, clearErrors]);

  useFormTracking({ formId, formErrors, isSuccess });

  useEffect(() => {
    if (isSuccess) {
      reset();
    }
  }, [reset, isSuccess]);

  return (
    <FormContainer>
      <CaptionBox>
        <Typography variant="p1">
          <Translate id="customer-platform.biddable-listing-page.bidding-form.paragraph" />
        </Typography>
        <Typography component="p" variant="caption2">
          <Translate id="customer-platform.biddable-listing-page.bidding-form.caption" />
        </Typography>
      </CaptionBox>
      <form
        id={formId}
        name={formId}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <FormBox>
          <FieldsWrapper>
            <TextField
              id="price"
              type="number"
              label={
                <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.price" />
              }
              slotProps={{ htmlInput: { min: 0 } }}
              {...register('price')}
              error={!!formErrors?.price}
              helperText={formErrors?.price?.message}
              required
              formId={formId}
            />
            <TextField
              id="quantity"
              type="number"
              label={
                <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.quantity" />
              }
              slotProps={{ htmlInput: { min: 0 } }}
              {...register('quantity')}
              error={!!formErrors?.quantity}
              helperText={formErrors?.quantity?.message}
              required
              formId={formId}
            />

            <Typography variant="p1">
              <Translate id="customer-platform.biddable-listing-page.bidding-form.your_data_subheading" />
            </Typography>

            <NameBox>
              <TextField
                id="first-name"
                label={
                  <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.first_name" />
                }
                {...register('firstName')}
                error={!!formErrors?.firstName}
                helperText={formErrors?.firstName?.message}
                required
                formId={formId}
              />
              <TextField
                id="last-name"
                label={
                  <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.last_name" />
                }
                {...register('lastName')}
                error={!!formErrors?.lastName}
                helperText={formErrors?.lastName?.message}
                required
                formId={formId}
              />
            </NameBox>
            <TextField
              id="company-name"
              label={
                <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.company_name" />
              }
              {...register('companyName')}
              error={!!formErrors?.companyName}
              helperText={formErrors?.companyName?.message}
              required
              formId={formId}
            />
            <TextField
              id="email"
              type="email"
              label={
                <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.email" />
              }
              {...register('email')}
              error={!!formErrors?.email}
              helperText={formErrors?.email?.message}
              required
              formId={formId}
            />
            <PhoneField
              id="phone-number"
              label={
                <Translate id="customer-platform.biddable-listing-page.bidding-form.textfield.phone_number" />
              }
              defaultCountry="DE"
              value={phoneNumberField.value}
              onChange={handleStringChange(
                phoneNumberField as ControllerRenderProps,
              )}
              error={!!formErrors.phoneNumber}
              helperText={formErrors?.phoneNumber?.message}
              required
              formId={formId}
            />

            <CheckBox
              id="terms-agreed"
              data-testid="bidding-form.check.test-agreement"
              label={
                <Translate
                  id="customer-platform.biddable-listing-page.bidding-form.checkbox.terms-text"
                  values={{
                    a: (chunks: string[]) => (
                      <Link
                        id="bidding-form.button.go-to-terms"
                        color={theme.palette.primary[500]}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${import.meta.env.VITE_STATIC_APP_URL}/terms-of-service`}
                      >
                        {head(chunks)}
                      </Link>
                    ),
                  }}
                />
              }
              value={termsAgreedField.value as boolean}
              onChange={handleCheckBoxChange}
              error={!!formErrors?.termsAgreed}
              helperText={formErrors?.termsAgreed?.message}
              formId={formId}
              size="small"
              required
            />
          </FieldsWrapper>

          <Button
            buttonType="primary"
            id="bidding-form.button.submit"
            data-testid="bidding-form.button.submit"
            size="large"
            type="submit"
            isSubmitting={isPending}
          >
            <Translate id="customer-platform.biddable-listing-page.bidding-form.button.submit" />
          </Button>
        </FormBox>
      </form>
    </FormContainer>
  );
}
